/* Built In Imports */
import NextLink from 'next/link';
import { useState, useRef, useEffect } from 'react';
/* External Imports */
import { Box, Link, Text, Show, Image } from '@chakra-ui/react';
import { render } from 'datocms-structured-text-to-html-string';
import parse from 'html-react-parser';
import { nanoid } from 'nanoid';
import { useMediaQuery } from 'react-responsive';

/* Internal Imports */
/* Components */
import config from '@config';
import { ShortCodeProcess } from '@components/Utility/ArticleParser/ShortCode';
import StructuredTextParser from '@components/Utility/ArticleParser/StructuredTextParser';
import { refineUrlDomain } from '@components/Utility/utils';
import useDynamicRefs from '@hooks/useDynamicRefs';

/* Services */
/* Styles */

/**
 * Renders VolunteerSharingTopSliderGroup Component
 *
 * @param {object} sectionContent - Data from api
 * @param {string} region - Region of page
 * @param {string} lang - Language of page
 * @returns
 */

const DonateFeature = ({ sectionContent, region, lang }) => {
  const [getRef, setRef] = useDynamicRefs();
  const isDesktop = useMediaQuery({ minWidth: 990 });
  let readLessTxtCount = '140';
  const descText =
    sectionContent?.cards[0]?.description?.value?.document?.children[0]
      ?.children[0]?.value;

  const ReadMoreLess = ({ text, lines = 4 }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const textRef = useRef(null);

    useEffect(() => {
      if (textRef.current) {
        const lineHeight = parseFloat(
          getComputedStyle(textRef.current).lineHeight
        );
        const maxHeight = lineHeight * lines;
        setIsOverflowing(textRef.current.scrollHeight > maxHeight);
      }
    }, []);

    return (
      <div className="max-w-md">
        <Box
          as="p"
          ref={textRef}
          className={`overflow-hidden transition-all ${
            isExpanded ? 'line-clamp-none' : ''
          }`}
          style={{
            display: '-webkit-box',
            WebkitLineClamp: isExpanded ? 'unset' : lines,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {text}
        </Box>

        {isOverflowing && (
          <button
            className="readmore mt-2 cursor-pointer"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? 'Read Less' : 'Read More'}
          </button>
        )}
      </div>
    );
  };

  const ctaButton = (buttonText, buttonLink, indx, groupcard) => {
    let buttonUrl = '';
    buttonUrl = ShortCodeProcess(buttonLink.toString());
    buttonUrl = parse(buttonUrl);
    return (
      <NextLink
        href={refineUrlDomain(buttonUrl, region, lang)}
        passHref
        legacyBehavior
      >
        <Link
          mb={{ md: '10px', base: '16px' }}
          display="flex"
          justifyContent="center"
          alignItems="center"
          fontWeight={500}
          fontFamily="FedraSansStd-medium"
          fontSize="16px"
          bgColor="orange.50"
          textAlign="center"
          padding="10px"
          borderRadius="3px"
          color="white"
          textDecoration="none"
          _hover={{
            bg: 'blue.50',
          }}
          cursor="pointer"
          width={'290px'}
          m="0 auto"
          height={{ base: '46px', md: '46px' }}
        >
          {buttonText}
        </Link>
      </NextLink>
    );
  };

  return (
    <>
      {sectionContent.cards.map((card, index) => {
        return (
          <Box
            as="article"
            display="flex"
            bgColor="#F5F5F5"
            borderRadius="5px"
            mb="30px"
            key={nanoid()}
            flexDir={{ base: 'column', lg: 'row' }}
            maxW={{
              base: '320px',
              sm: '480px',
              md: '690px',
              lg: '770px',
              xl: '1200px',
            }}
            w="full"
            h="full"
            mx="auto"
            _last={{
              mb: { base: '10', lg: '70' },
            }}
          >
            <Box maxW={{ base: '100%', lg: '260px', xl: '260px' }} minH={{base:'220px', md:'260px'}} w="100%">
              <Box
                bgImage={{
                  base: card.mobileThumbnail?.url,
                  lg: card.thumbnail?.url,
                }}
                bgRepeat="no-repeat"
                bgSize="cover"
                borderRadius={{
                  base: '8px 8px 0px 0px',
                  lg: '8px 0px 0px 8px',
                }}
                w="full"
                h={{ base: '221', md: '318', lg: 'full' }}
                pos="relative"
              >
                {card.isFeatured && (
                  <Box
                    pos="absolute"
                    bgColor="#CF4520"
                    color="#fff"
                    top="10px"
                    fontSize="16px"
                    p="0px 4px 0px 10px"
                    fontFamily="FedraSansStd-book"
                    _before={{
                      content: '" "',
                      height: 0,
                      width: 0,
                      display: 'block',
                      position: 'absolute',
                      top: 0,
                      right: '-12px',
                      borderTop: '12px solid #CF4520',
                      borderBottom: '12px solid #CF4520',
                      borderRight: '12px solid transparent',
                      borderLeft: '12px solid transparent',
                    }}
                  >
                    Featured
                  </Box>
                )}
              </Box>
            </Box>
            <Box
              display="flex"
              flexDir={{ base: 'column', md: 'row' }}
              gridGap={{ base: '30px', md: '80px' }}
              width={{ base: '100%', md: 'full' }}
              p={{
                base: '24px 15px 12px 15px',
                sm: '27px 30px',
                lg: '27px 48px',
              }}
            >
              <Box
                w={{ base: '100%', lg: '70%' }}
                // p={{ md: '27px 36px 26px 48px', base: '24px 0 24px 0' }}
              >
                <Box
                  as="h2"
                  fontFamily="FedraSansStd-A-medium"
                  fontStyle="normal"
                  fontWeight="500"
                  fontSize={{ base: '22px', md: '28px' }}
                  lineHeight={{ base: '26px', md: '36px' }}
                  pb="15px"
                >
                  {card.title}
                </Box>
                <Box
                  as="p"
                  lineHeight="26px"
                  fontFamily="FedraSansStd-book"
                  ref={setRef(index + 1)}
                  id={index + 1}
                >
                  {isDesktop ? (
                    <>
                    <StructuredTextParser
                      str={render(card?.description)}
                      region={region}
                      lang={lang}
                      mt="0"
                      fontSize="18px"
                    /></>
                  ) : (
                    <Box className="card_description"><ReadMoreLess text={descText} lines={4} /></Box>
                  )}
                </Box>
              </Box>
              <Box
                w={{ base: '100%', lg: '46%' }}
                pt={{ base: '0', sm: '10px' }}
              >
                {card.buttonDescription && (
                  <Box
                    fontSize="16px"
                    fontFamily="FedraSansStd-book"
                    mb="2"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gridGap="6px"
                  >
                    <LazyLoadImageComponent
                      src={card?.buttonIcon?.url}
                      title={card?.buttonIcon?.title}
                      alt={card?.buttonIcon?.alt}
                    />
                    {card.buttonDescription}
                  </Box>
                )}
                {card.buttonText && (
                  <Box>{ctaButton(card?.buttonText, card?.buttonLink)}</Box>
                )}

                {card.secondCtaDescription && (
                  <Box
                    fontSize="16px"
                    fontFamily="FedraSansStd-book"
                    mb="2"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gridGap="6px"
                  >
                    <LazyLoadImageComponent
                      src={card?.secondCtaIcon?.url}
                      title={card?.secondCtaIcon?.title}
                      alt={card?.secondCtaIcon?.alt}
                    />
                    {card.secondCtaDescription}
                  </Box>
                )}
                {card.secondCtaLabel && (
                  <Box>
                    {ctaButton(card?.secondCtaLabel, card?.secondCtaLink)}
                  </Box>
                )}

                {card.thirdCtaDescription && (
                  <Box
                    fontSize="16px"
                    fontFamily="FedraSansStd-book"
                    mb="2"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gridGap="6px"
                  >
                    <LazyLoadImageComponent
                      src={card?.thirdCtaIcon?.url}
                      title={card?.thirdCtaIcon?.title}
                      alt={card?.thirdCtaIcon?.alt}
                    />
                    {card.thirdCtaDescription}
                  </Box>
                )}
                {card.thirdCtaLabel && (
                  <Box>
                    {ctaButton(card?.thirdCtaLabel, card?.thirdCtaLink)}
                  </Box>
                )}

                {card.fourthCtaDescription && (
                  <Box
                    fontSize="16px"
                    fontFamily="FedraSansStd-book"
                    mb="2"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gridGap="6px"
                  >
                    <LazyLoadImageComponent
                      src={card?.fourthCtaIcon?.url}
                      title={card?.fourthCtaIcon?.title}
                      alt={card?.fourthCtaIcon?.alt}
                    />
                    {card.fourthCtaDescription}
                  </Box>
                )}
                {card.fourthCtaLabel && (
                  <Box>
                    {ctaButton(card?.fourthCtaLabel, card?.fourthCtaLink)}
                  </Box>
                )}
              </Box>
            </Box>
            <style>
              {`
            
            @media (max-width: 768px) {
             .card_description {
              margin-top: 0px;
              min-height: 150px;
            }
            .card_description p {
              line-height: 30px;
              font-size: 16px;
              width: 272px;
              font-family: FedraSansStd-book;
            }
            }
          `}
            </style>
          </Box>
        );
      })}
    </>
  );
};

export default DonateFeature;
